// Admin → Users page. Gated by `can_manage_users` permission.
// Lists every user in the Clerk instance (plus pending invitations),
// lets admin invite new users (email or copyable link) with a preset
// role + team + rights, edit role/team/supervisor/disbursement-limit,
// deactivate/reactivate, resend or revoke invitations.
// Rights resolve LIVE from the portal role matrix — editing a role in
// Roles & Permissions applies to every user with that role instantly.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import RoleEditor from "@/pages/admin/RoleEditor";
import apiClient from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UserPlus, Shield, AlertTriangle, RotateCw, Trash2, Mail, Search, Link2, Copy, Check, XCircle } from "lucide-react";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import {
  ADMIN_ACCOUNT_NOTE,
  LEGACY_PP_LIMIT_NOTE,
  MONEY_GRANT_NOTE,
  PP_LIMITS_NOTE,
  SELF_EDIT_NOTE,
  isAdminAccount,
  hasLegacyPpLimit,
  isOwnRow,
  limitBoxPlaceholder,
  limitToStore,
  permissionTickableBy,
  ppLimitsChanged,
  ppLimitsError,
  ppLimitsLoosened,
  roleAssignableBy,
  storedLimitLabel,
} from "@/lib/userAccessRules";

const listUsers = async (q) =>
  (await apiClient.get("/admin/users", { params: q ? { q } : {} })).data;
const inviteUser   = async (p) => (await apiClient.post("/admin/users/invite", p)).data;
const updateUser   = async (id, p) => (await apiClient.patch(`/admin/users/${encodeURIComponent(id)}`, p)).data;
const deactivate   = async (id) => (await apiClient.post(`/admin/users/${encodeURIComponent(id)}/deactivate`)).data;
const reactivate   = async (id) => (await apiClient.post(`/admin/users/${encodeURIComponent(id)}/reactivate`)).data;
const resendInvite = async (id) => (await apiClient.post(`/admin/users/${encodeURIComponent(id)}/resend-invite`)).data;
const revokeInvite = async (id) => (await apiClient.post(`/admin/users/${encodeURIComponent(id)}/revoke-invite`)).data;

function roleBadgeTone(role) {
  if (!role) return "bg-slate-100 text-slate-600";
  if (role === "Admin") return "bg-red-100 text-red-800 border-red-300";
  if (role.includes("Manager")) return "bg-amber-100 text-amber-800 border-amber-300";
  if (role.includes("TL")) return "bg-blue-100 text-blue-800 border-blue-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  }
};

export default function AdminUsers() {
  const { hasPermission, rolesCatalog, user: me } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "roles" ? "roles" : "users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [inviteResult, setInviteResult] = useState(null); // { email, invite_url }

  const canManage = hasPermission("can_manage_users");

  const reload = async (q) => {
    setLoading(true); setError(null);
    try {
      const data = await listUsers(q);
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(errorDetailMessage(e, e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canManage) reload(); }, [canManage]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name  || "").toLowerCase().includes(q) ||
      (u.role  || "").toLowerCase().includes(q) ||
      (u.team  || "").toLowerCase().includes(q) ||
      (Array.isArray(u.teams) ? u.teams.join(" ") : "").toLowerCase().includes(q)
    );
  }, [users, search]);

  if (!canManage) {
    return (
      <div className="p-6">
        <Card><CardContent className="py-10 text-center">
          <Shield className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-700">You don't have permission to manage users.</p>
          <p className="text-xs text-muted-foreground mt-1">Required: <code>can_manage_users</code></p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="admin-users-page">
      <div className="flex rounded-lg border overflow-hidden w-fit" data-testid="admin-users-tabs">
        <button type="button"
          className={`px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 ${tab === "users" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setTab("users")} data-testid="admin-tab-users">
          <UserPlus className="w-3.5 h-3.5" />Users
        </button>
        <button type="button"
          className={`px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 ${tab === "roles" ? "bg-primary text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setTab("roles")} data-testid="admin-tab-roles">
          <Shield className="w-3.5 h-3.5" />Roles &amp; Permissions
        </button>
      </div>
      {tab === "roles" ? (
        <RoleEditor />
      ) : (
        <>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
          <p className="text-sm text-muted-foreground">
            Invite, edit and deactivate portal users. Backed by Clerk — rights update live from the role matrix.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => reload()} disabled={loading} data-testid="users-refresh">
            <RotateCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
          </Button>
          <Button onClick={() => setInviteOpen(true)} data-testid="users-invite-btn">
            <UserPlus className="w-3.5 h-3.5 mr-1" /> Invite user
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="py-3 flex items-start gap-2 text-red-900 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Couldn't load users from Clerk:</div>
              <div className="text-xs font-mono">{error}</div>
              <div className="text-xs mt-1 text-red-700">Tip: this usually means the Clerk Secret Key isn't configured — set it in Portal Settings → API Credentials → Clerk.</div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm">
              Users <Badge variant="outline" className="text-[10px] ml-1">{filteredUsers.length}</Badge>
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search email, name, role, team…"
                className="h-8 text-xs w-72 pl-7"
                data-testid="users-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No users found. Click "Invite user" to add the first one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="text-left p-2">User</th>
                    <th className="text-left p-2">Role</th>
                    <th className="text-left p-2">Team</th>
                    <th className="text-right p-2">Limit</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Last active</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.user_id} className="border-b hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                      <td className="p-2">
                        <div className="font-medium text-slate-900 truncate max-w-[260px]" title={u.email}>{u.name || u.email}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-[260px]">{u.email}</div>
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className={`text-[10px] ${roleBadgeTone(u.role)}`}>
                          {u.role || "— no role —"}
                        </Badge>
                        {u.has_override && (
                          <Badge variant="outline" className="text-[9px] ml-1 bg-violet-100 text-violet-700" title="Per-user permission override active">
                            custom
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 text-slate-700">
                        {(Array.isArray(u.teams) && u.teams.length ? u.teams : [u.team]).filter(Boolean).join(", ") || "—"}
                        {Array.isArray(u.regions) && u.regions.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-0.5" data-testid={`user-regions-${u.user_id}`}>
                            {u.regions.map(r => (
                              <span key={r}
                                className={`text-[9px] font-medium px-1 py-0.5 rounded ${
                                  r === "UK" ? "bg-indigo-50 text-indigo-700 border border-indigo-200"
                                             : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                }`}>
                                {r}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right tabular-nums">
                        {u.disbursement_limit != null ? `£${Number(u.disbursement_limit).toLocaleString()}` : "—"}
                        {(Number(u.topup_limits?.UK) > 0 || Number(u.topup_limits?.AUS) > 0) && (
                          <div className="text-[10px] text-amber-700" data-testid="user-topup-limits">
                            Top-up: {Number(u.topup_limits?.UK) > 0 ? `£${Number(u.topup_limits.UK).toLocaleString()}` : "—"}{Number(u.topup_limits?.AUS) > 0 ? ` · A$${Number(u.topup_limits.AUS).toLocaleString()}` : ""}
                          </div>
                        )}
                        {Number(u.disbursement_limit_aud) > 0 && (
                          <div className="text-[10px] text-emerald-700">A${Number(u.disbursement_limit_aud).toLocaleString()} AUD</div>
                        )}
                        {u.can_view_payout_balance && (
                          <div className="text-[9px] text-blue-700" title="Can view live GoCardless/Capay payout balances">Balance view</div>
                        )}
                        {u.pp_limits && (
                          <div className="text-[9px] text-orange-700" title="Payment plan limits: min pay % / max discount % / max weeks">
                            PP: {[
                              u.pp_limits.min_pay_pct != null ? `≥${u.pp_limits.min_pay_pct}%` : null,
                              u.pp_limits.max_discount_pct != null ? `disc ≤${u.pp_limits.max_discount_pct}%` : null,
                              u.pp_limits.max_duration_weeks != null ? `≤${u.pp_limits.max_duration_weeks}w` : null,
                            ].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        {u.blocked
                          ? <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600">Deactivated</Badge>
                          : u.is_invitation
                            ? <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800">Invite pending</Badge>
                            : <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800">Active</Badge>}
                      </td>
                      <td className="p-2 text-muted-foreground text-[10px]"
                          title={`Last activity: any portal usage, even a reopened session.\nLast formal login: ${u.last_login ? new Date(u.last_login).toLocaleString("en-GB") : "never"}`}
                          data-testid={`user-last-active-${u.user_id}`}>
                        {(u.last_active || u.last_login)
                          ? new Date(u.last_active || u.last_login).toLocaleString("en-GB")
                          : "Never"}
                        {u.last_active && (
                          <div className="text-[9px] text-emerald-700">active</div>
                        )}
                      </td>
                      <td className="p-2 text-right whitespace-nowrap">
                        {!u.is_invitation && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setEditTarget(u)} data-testid={`edit-user-${u.user_id}`}>Edit</Button>
                        )}
                        {u.is_invitation && (
                          <>
                            {u.invite_url && (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={async () => {
                                await copyToClipboard(u.invite_url);
                                toast.success("Invite link copied to clipboard");
                              }} data-testid={`copy-invite-link-${u.user_id}`}>
                                <Link2 className="w-3 h-3 mr-1" />Copy link
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={async () => {
                              try {
                                const r = await resendInvite(u.user_id);
                                toast.success(`Invite re-sent to ${u.email}`);
                                if (r?.invite_url) setInviteResult({ email: u.email, invite_url: r.invite_url });
                                reload();
                              }
                              catch(e){ toast.error(errorDetailMessage(e, e.message)); }
                            }} data-testid={`resend-invite-${u.user_id}`}>
                              <Mail className="w-3 h-3 mr-1" />Resend
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-red-600" onClick={async () => {
                              if (!window.confirm(`Revoke the invitation for ${u.email}?`)) return;
                              try { await revokeInvite(u.user_id); toast.success("Invitation revoked"); reload(); }
                              catch(e){ toast.error(errorDetailMessage(e, e.message)); }
                            }} data-testid={`revoke-invite-${u.user_id}`}>
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                        {!u.is_invitation && u.user_id !== me?.user_id && (
                          u.blocked ? (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-emerald-700" onClick={async () => {
                              await reactivate(u.user_id); toast.success("User reactivated"); reload();
                            }}>Reactivate</Button>
                          ) : (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-red-600" onClick={async () => {
                              if (!window.confirm(`Deactivate ${u.email}? They won't be able to log in.`)) return;
                              await deactivate(u.user_id); toast.success("User deactivated"); reload();
                            }} data-testid={`deactivate-${u.user_id}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {inviteOpen && (
        <UserFormDialog
          mode="invite"
          rolesCatalog={rolesCatalog}
          onClose={() => setInviteOpen(false)}
          onSaved={(res) => {
            setInviteOpen(false);
            if (res?.invite_url) setInviteResult({ email: res.email, invite_url: res.invite_url });
            reload();
          }}
        />
      )}
      {editTarget && (
        <UserFormDialog
          mode="edit"
          user={editTarget}
          rolesCatalog={rolesCatalog}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); reload(); }}
        />
      )}
      {inviteResult && (
        <InviteLinkDialog result={inviteResult} onClose={() => setInviteResult(null)} />
      )}
        </>
      )}
    </div>
  );
}


// ───────────────────────── Invite-link dialog ─────────────────────────
function InviteLinkDialog({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="invite-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-600" /> Invitation link ready
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Share this link with <span className="font-medium">{result.email}</span> — it lets them
            set their password and sign in with the role you assigned. The link expires when the
            invitation is revoked or accepted.
          </p>
          <div className="flex items-center gap-2">
            <Input readOnly value={result.invite_url} className="text-xs font-mono h-9" data-testid="invite-link-input" onFocus={e => e.target.select()} />
            <Button size="sm" className="h-9 shrink-0" onClick={async () => {
              await copyToClipboard(result.invite_url);
              setCopied(true);
              toast.success("Invite link copied");
              setTimeout(() => setCopied(false), 2000);
            }} data-testid="invite-link-copy-btn">
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="invite-link-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ───────────────────────── Invite / Edit dialog ─────────────────────────
const teams_catalog = (rolesCatalog) => rolesCatalog?.teams || [];

function UserFormDialog({ mode, user, rolesCatalog, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail]               = useState(user?.email || "");
  const [name,  setName]                = useState(user?.name  || "");
  const [role,  setRole]                = useState(user?.role  || "");
  const [teams, setTeams]               = useState(Array.isArray(user?.teams) && user.teams.length ? user.teams : (user?.team ? [user.team] : []));
  const [supervisorId, setSupervisorId] = useState(user?.supervisor_id || "");
  const [limit, setLimit]               = useState(user?.disbursement_limit ?? "");
  const [limitAud, setLimitAud]         = useState(user?.disbursement_limit_aud ?? "");
  const [topupUk, setTopupUk]           = useState(user?.topup_limits?.UK ?? "");
  const [topupAud, setTopupAud]         = useState(user?.topup_limits?.AUS ?? "");
  const [canViewBalance, setCanViewBalance] = useState(Boolean(user?.can_view_payout_balance));
  const [ppMinPct, setPpMinPct]         = useState(user?.pp_limits?.min_pay_pct ?? "");
  const [ppMaxDisc, setPpMaxDisc]       = useState(user?.pp_limits?.max_discount_pct ?? "");
  const [ppMaxWeeks, setPpMaxWeeks]     = useState(user?.pp_limits?.max_duration_weeks ?? "");
  const [sendEmail, setSendEmail]       = useState(true);
  const [overrideOn, setOverrideOn]     = useState(Boolean(user?.has_override));
  const [customPerms, setCustomPerms]   = useState(user?.permissions || []);
  const [regions, setRegions]           = useState(Array.isArray(user?.regions) && user.regions.length ? user.regions : ["UK", "AUS"]);
  const [saving, setSaving] = useState(false);

  // The backend refuses these changes whatever the screen offers; this keeps the screen from
  // offering them. See `lib/userAccessRules.js`.
  const { user: me, isAdmin } = useAuth();
  const admin = isAdmin();
  const ownRow = isEdit && isOwnRow(me, user);
  const adminRowForNonAdmin = isEdit && !admin && isAdminAccount(user);
  const locked = ownRow || adminRowForNonAdmin;
  const heldPermissions = isEdit ? user?.permissions || [] : [];
  const currentLimit = isEdit ? user?.disbursement_limit ?? 0 : 0;
  const limitContext = { isEdit, storedLimit: user?.disbursement_limit };

  const roles = rolesCatalog?.roles || [];
  const availableRegions = rolesCatalog?.regions || ["UK", "AUS"];
  const rolesWithAllRegions = rolesCatalog?.roles_with_all_regions || ["Admin", "Operations Manager"];
  const permsGlossary = rolesCatalog?.permissions || {};
  const forceAllRegions = rolesWithAllRegions.includes(role);
  const effectiveRegions = forceAllRegions ? availableRegions : regions;

  // When the role changes, snap the default disbursement_limit + reset perms unless overriden
  const selectedRoleInfo = roles.find(r => r.name === role);
  useEffect(() => {
    if (!isEdit && selectedRoleInfo) {
      setLimit(selectedRoleInfo.default_disbursement_limit);
      setCustomPerms(selectedRoleInfo.permissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const togglePerm = (p) => {
    setCustomPerms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const toggleRegion = (r) => {
    setRegions(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  };

  const handleSave = async () => {
    if (!isEdit && !email) { toast.error("Email is required"); return; }
    if (!role)             { toast.error("Role is required"); return; }
    if (!forceAllRegions && (!regions || regions.length === 0)) {
      toast.error("Please tick at least one region — otherwise this user will have no region access.");
      return;
    }
    const limitToSend = limitToStore(limit, limitContext);
    if (!admin && limitToSend !== null && limitToSend > currentLimit) { toast.error(MONEY_GRANT_NOTE); return; }
    const ppLimits = {
      min_pay_pct:        ppMinPct  !== "" ? Number(ppMinPct)  : null,
      max_discount_pct:   ppMaxDisc !== "" ? Number(ppMaxDisc) : null,
      max_duration_weeks: ppMaxWeeks !== "" ? Number(ppMaxWeeks) : null,
    };
    // An edit sends the limits only if they were changed. An old stored value the backend
    // now refuses as input (0 or negative) is shown as it is and must not be re-sent, or the
    // user could not be edited for anything else.
    const sendPpLimits = !isEdit || ppLimitsChanged(user?.pp_limits, ppLimits);
    if (sendPpLimits) {
      const ppError = ppLimitsError(ppLimits);
      if (ppError) { toast.error(ppError); return; }
      if (isEdit && !admin && ppLimitsLoosened(user?.pp_limits, ppLimits)) { toast.error(PP_LIMITS_NOTE); return; }
    }
    const payload = {
      name: name || null,
      role,
      team: teams[0] || null,
      teams,
      supervisor_id: supervisorId || null,
      disbursement_limit: limitToSend,
      disbursement_limit_aud: limitAud !== "" && limitAud !== null ? Number(limitAud) : null,
      topup_limits: { UK: Number(topupUk) || 0, AUS: Number(topupAud) || 0 },
      can_view_payout_balance: canViewBalance,
      ...(sendPpLimits ? { pp_limits: ppLimits } : {}),
      permissions_override: overrideOn ? customPerms : null,
      regions: forceAllRegions ? availableRegions : regions,
    };
    setSaving(true);
    try {
      if (isEdit) {
        const res = await updateUser(user.user_id, payload);
        if (res?.warning) toast.warning(res.warning);
        else toast.success(`Updated ${user.email}. Disbursement limit: ${storedLimitLabel(res?.disbursement_limit)}`);
        onSaved?.(res);
      } else {
        const res = await inviteUser({ email, send_email: sendEmail, ...payload });
        toast.success(sendEmail ? `Invitation emailed to ${email}` : `Invite link created for ${email}`);
        onSaved?.(res);
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${user?.email}` : "Invite new user"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div>
              <label className="text-xs font-medium text-slate-700">Email</label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="someone@simplyfunded.co.uk" data-testid="invite-email" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-700">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" data-testid="invite-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Role</label>
              <select value={role} onChange={e => setRole(e.target.value)} disabled={locked} className="w-full border rounded h-9 px-2 text-sm disabled:opacity-60" data-testid="invite-role">
                <option value="">— select —</option>
                {roles.map(r => (
                  <option key={r.name} value={r.name} disabled={r.name !== role && !roleAssignableBy(admin, r)}>
                    {r.name} ({r.department})
                  </option>
                ))}
              </select>
              {selectedRoleInfo && (
                <div className="text-[10px] text-muted-foreground mt-0.5 italic">{selectedRoleInfo.description}</div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Teams <span className="text-muted-foreground font-normal">(tick every team they work in)</span></label>
              <div className="border rounded p-2 grid grid-cols-2 gap-x-3 gap-y-1 max-h-32 overflow-y-auto" data-testid="invite-team">
                {teams_catalog(rolesCatalog).map(t => (
                  <label key={t} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input type="checkbox" checked={teams.includes(t)}
                      onChange={() => setTeams(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                      data-testid={`invite-team-${t.replace(/[^A-Za-z]+/g, "-").replace(/-+$/, "").toLowerCase()}`} />
                    {t}
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {teams.length ? `Primary: ${teams[0]} — the first ticked team is used for sign-offs; Legal / Collections teams make them assignable for those actions.` : "No team — regions then come from the ticks below."}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Supervisor (Clerk user id)</label>
              <Input value={supervisorId} onChange={e => setSupervisorId(e.target.value)} placeholder="user_2abc…" data-testid="invite-supervisor" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Disbursement limit (£)</label>
              <Input type="number" value={limit} onChange={e => setLimit(e.target.value)} placeholder={limitBoxPlaceholder(limitContext)}
                disabled={locked || (!admin && !isEdit)} max={admin ? undefined : currentLimit} data-testid="invite-limit" />
              {isEdit && (
                <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="limit-box-note">
                  An empty box saves as shown. Changing the role does not change a limit that is already set.
                </div>
              )}
              <label className="text-xs font-medium text-slate-700 mt-2 block">AUS disbursement limit (A$)</label>
              <Input type="number" value={limitAud} onChange={e => setLimitAud(e.target.value)} placeholder="0" data-testid="invite-limit-aud" />
              <div className="text-[10px] text-muted-foreground mt-0.5">Approval ceiling for Australian (Capay) payouts</div>
              <div className="grid grid-cols-2 gap-2 mt-2 rounded border border-amber-200 bg-amber-50/50 p-2" data-testid="invite-topup-limits">
                <div>
                  <label className="text-[11px] font-medium text-amber-900">Top-up limit UK (£)</label>
                  <Input type="number" value={topupUk} onChange={e => setTopupUk(e.target.value)} placeholder="same as new deals" className="h-8" data-testid="invite-topup-limit-uk" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-amber-900">Top-up limit AUS (A$)</label>
                  <Input type="number" value={topupAud} onChange={e => setTopupAud(e.target.value)} placeholder="same as new deals" className="h-8" data-testid="invite-topup-limit-aud" />
                </div>
                <div className="col-span-2 text-[10px] text-amber-800">Used instead of the limits above when the deal is a top-up (SF# /2, /3… or a prior loan being settled). Leave 0 to use the new-deal limit.</div>
              </div>
              <label className="text-[11px] flex items-center gap-1.5 cursor-pointer mt-2">
                <input type="checkbox" checked={canViewBalance} onChange={e => setCanViewBalance(e.target.checked)} data-testid="invite-can-view-balance" />
                Can view live payout balances (GoCardless / Capay)
              </label>
            </div>
          </div>
          <div className="border rounded p-2 bg-blue-50/40" data-testid="invite-regions-block">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-slate-700">Region access</div>
              {forceAllRegions && (
                <span className="text-[10px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                  Locked to all regions for {role}
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mb-1.5">
              Which regions this user can work in. Untick a region to hide it in the region switcher and block its APIs (AUS deals, Capay, etc.).
            </div>
            <div className="flex flex-wrap gap-2">
              {availableRegions.map(r => {
                const checked = effectiveRegions.includes(r);
                return (
                  <label key={r}
                    className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer ${
                      checked ? "bg-white border-slate-300" : "bg-slate-50 border-slate-200"
                    } ${forceAllRegions ? "opacity-70 cursor-not-allowed" : ""}`}
                    data-testid={`invite-region-${r}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={forceAllRegions}
                      onChange={() => toggleRegion(r)}
                    />
                    <span className="font-medium">{r === "AUS" ? "🇦🇺 AUS" : "🇬🇧 UK"}</span>
                    <span className="text-muted-foreground">{r === "AUS" ? "Australia" : "United Kingdom"}</span>
                  </label>
                );
              })}
            </div>
          </div>
          {ownRow && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="self-edit-note">
              {SELF_EDIT_NOTE}
            </div>
          )}
          {adminRowForNonAdmin && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="admin-account-note">
              {ADMIN_ACCOUNT_NOTE}
            </div>
          )}
          {!locked && !admin && (
            <div className="text-[11px] text-muted-foreground" data-testid="money-grant-note">
              {MONEY_GRANT_NOTE} You can lower a limit, remove a permission or tighten payment plan limits.
            </div>
          )}
          <div className="border rounded p-2 bg-orange-50/40">
            <div className="text-xs font-medium text-slate-700">Payment plan limits</div>
            <div className="text-[10px] text-muted-foreground mb-1.5">
              Plans this user builds beyond these limits save as <strong>pending approval</strong> (manager sign-off + Slack alert). Leave a box blank for no limit on that measure; clearing one box removes only that limit.
            </div>
            {isEdit && hasLegacyPpLimit(user?.pp_limits) && (
              <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5 mb-1.5" data-testid="legacy-pp-limit-note">
                {LEGACY_PP_LIMIT_NOTE}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-medium text-slate-600">Min payment %</label>
                <Input type="number" value={ppMinPct} onChange={e => setPpMinPct(e.target.value)} placeholder="e.g. 40" disabled={locked} data-testid="invite-pp-min-pct" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-600">Max discount %</label>
                <Input type="number" value={ppMaxDisc} onChange={e => setPpMaxDisc(e.target.value)} placeholder="e.g. 5" disabled={locked} data-testid="invite-pp-max-discount" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-600">Max duration (wks)</label>
                <Input type="number" value={ppMaxWeeks} onChange={e => setPpMaxWeeks(e.target.value)} placeholder="e.g. 8" disabled={locked} data-testid="invite-pp-max-weeks" />
              </div>
            </div>
          </div>
          {role && (
            <div className="border rounded p-2 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-slate-700">
                  Permissions ({selectedRoleInfo?.permissions.length || 0} default for {role})
                </div>
                <label className="text-[11px] flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={overrideOn} onChange={e => {
                    setOverrideOn(e.target.checked);
                    if (!e.target.checked) setCustomPerms(selectedRoleInfo?.permissions || []);
                  }} disabled={locked} data-testid="invite-override-perms" />
                  Override defaults
                </label>
              </div>
              <div className="text-[10px] text-muted-foreground mb-1.5">
                Rights follow the role's live matrix (Roles &amp; Permissions page) unless overridden per-user here — tick/untick to add or remove rights just for this user.
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                {(rolesCatalog?.sections?.length
                  ? rolesCatalog.sections
                  : [{ section: "All permissions", items: Object.keys(permsGlossary) }]
                ).map(({ section, items }) => {
                  const visible = (items || []).filter(p => permsGlossary[p] !== undefined);
                  if (!visible.length) return null;
                  const checkedCount = visible.filter(p => customPerms.includes(p)).length;
                  const allOn = checkedCount === visible.length;
                  return (
                    <div key={section}>
                      <div className="flex items-center justify-between">
                        <div className="text-[11px] font-semibold text-slate-700">
                          {section} <span className="font-normal text-muted-foreground">{checkedCount}/{visible.length}</span>
                        </div>
                        {overrideOn && (
                          <button type="button"
                            className="text-[10px] text-primary hover:underline"
                            onClick={() => setCustomPerms(prev => allOn
                              ? prev.filter(p => !visible.includes(p))
                              : [...new Set([...prev, ...visible])])}>
                            {allOn ? "Untick all" : "Tick all"}
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-1 mt-0.5">
                        {visible.map(p => {
                          const checked = customPerms.includes(p);
                          return (
                            <label key={p} className={`text-[10px] flex items-start gap-1.5 p-1 rounded ${overrideOn ? "cursor-pointer hover:bg-white" : "opacity-50"}`} title={p}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!overrideOn || locked || (!checked && !permissionTickableBy(admin, p, heldPermissions, me?.permissions))}
                                onChange={() => togglePerm(p)}
                              />
                              <span>{permsGlossary[p]}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!isEdit && (
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer border rounded p-2 bg-blue-50/50">
              <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} data-testid="invite-send-email" />
              <span>
                <span className="font-medium">Send email invite</span>
                <span className="text-muted-foreground"> — untick to create a link-only invite you can copy and share yourself</span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="invite-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {isEdit ? "Save changes" : (sendEmail ? "Send invitation" : "Create invite link")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
